import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ConnectionFlow from '@/kenwood/configurator/components/ConnectionFlow';
import { useConfiguratorContext } from '@/kenwood/configurator/context';
import useCarouselRef from '@/kenwood/configurator/hooks/useCarouselRef';

const ProductsCarousel = () => {
  const { t } = useTranslation();
  const [{ isOwner }] = useConfiguratorContext();
  const [{ productsData }] = useConfiguratorContext();
  const carouselDesc = useRef();

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [slides, setSlides] = useState([]);

  const imageFallback = `${DLG.config.distResourcePath}/assets/images/kenwood/configurator/category-overview.png`;

  const scrollToDesc = () => {
    carouselDesc.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const selectColorVariant = useCallback(
    variant => {
      const newSlides = [...slides];
      const newSlide = { ...newSlides[carouselIndex] };
      newSlide.activeVariant = variant;
      newSlides[carouselIndex] = newSlide;
      setSlides(newSlides);
    },
    [slides, carouselIndex],
  );

  useEffect(() => {
    const newSlides = [];

    if (productsData) {
      if (isOwner) {
        productsData.forEach(c => {
          if (c?.isOwnerCategory) {
            newSlides.push({
              code: c.code,
              description: c.description,
              image: c.image,
              name: c.name,
              price: null,
              element: c,
            });
          }
        });
      } else {
        productsData.forEach(c => {
          if (!c?.isOwnerCategory) {
            // Check if this category has products
            if (c.products.length > 0) {
              // One category equals to one slide
              // One category equals to one base product
              const slide = {
                // We display the name of the category as the name of the slide
                name: c.name,
                variants: [],
                activeVariant: null,
              };

              // Now that we have all variants we can create the slide with the variants
              c.products.forEach(p => {
                const {
                  code,
                  shortDescription,
                  price,
                  dlgConfiguratorImages,
                  stock,
                  colorHexList,
                  colorName,
                } = p;

                // Check if the product has a color variant, a price and is in stock
                if (
                  colorHexList &&
                  colorName &&
                  price &&
                  stock?.stockLevelStatus !== 'outOfStock'
                ) {
                  // Add the product to the slide
                  slide.variants.push({
                    code,
                    description: shortDescription,
                    price,
                    image:
                      dlgConfiguratorImages.find(i => i.format === 'product') ||
                      null,
                    colorHexList,
                    colorName,
                    element: p,
                  });
                }
              });

              // Check if the slide has variants
              if (slide.variants.length > 0) {
                // Set the active variant to the first one
                const activeVariant = slide.variants[0];
                slide.activeVariant = activeVariant;

                // Add the slide to the slides array
                newSlides.push(slide);
              }
            }
          }
        });
      }
    }

    setSlides(newSlides);
  }, [productsData, isOwner]);

  const carouselRef = useCarouselRef(setCarouselIndex);

  return (
    slides.length > 0 && (
      <div className='ken-configurator__carousel'>
        <div className='ken-configurator__carousel--wrapper'>
          <h2 className='ken-configurator__carousel--slide-title'>
            {slides[carouselIndex].name}
          </h2>
          <div className='ken-configurator__carousel--swiper'>
            <div ref={carouselRef} className='swiper-container'>
              <button
                type='button'
                className='ken-configurator__carousel--link-to-details'
                onClick={scrollToDesc}
              >
                {t(
                  'configurator:configurator.productscarousel.link.to.details',
                )}
              </button>
              <button type='button' className='swiper-button-prev'>
                <img
                  src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/arrow.svg`}
                  alt='prev'
                />
              </button>
              <div className='swiper-wrapper'>
                {slides.map((s, i) => (
                  <div
                    key={`slide-image-${s.name ? s.name : i}`}
                    className='ken-configurator__carousel--slide-image swiper-slide'
                  >
                    <img
                      alt={
                        isOwner
                          ? s.image?.m?.altText
                          : s.activeVariant.image?.m?.altText || s.name
                      }
                      src={
                        isOwner
                          ? s.image?.m?.url || imageFallback
                          : s.activeVariant.image?.m?.url || imageFallback
                      }
                    />
                  </div>
                ))}
              </div>
              <button type='button' className='swiper-button-next'>
                <img
                  src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/arrow.svg`}
                  alt='next'
                />
              </button>
            </div>
          </div>
          <div className='ken-configurator__carousel--slide-indicator'>
            {carouselIndex + 1}&nbsp;
            {t(
              'configurator:configurator.productscarousel.slide.indicator.label',
            )}
            &nbsp;{slides.length}
          </div>
          <div
            ref={carouselDesc}
            className='ken-configurator__carousel--slide-description'
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: isOwner
                ? slides[carouselIndex].description
                : slides[carouselIndex].activeVariant.description,
            }}
          />
          {slides[carouselIndex].variants && (
            <div className='ken-configurator__carousel--color-swatch'>
              <span className='ken-configurator__carousel--color-swatch-label'>
                {t('configurator:configurator.productscarousel.color.label')}
              </span>
              <div className='ken-configurator__carousel--color-swatch-wrapper'>
                {slides[carouselIndex].variants.map(variant => (
                  <button
                    type='button'
                    className={`ken-configurator__carousel--color-swatch-item ${
                      variant.code === slides[carouselIndex].activeVariant.code
                        ? '--active'
                        : ''
                    }`}
                    onClick={() => selectColorVariant(variant)}
                    key={variant.code}
                  >
                    <span
                      className={`ken-configurator__carousel--color-swatch-item-color ${
                        variant.code ===
                        slides[carouselIndex].activeVariant.code
                          ? '--active'
                          : ''
                      }`}
                      style={{
                        backgroundColor: `#${variant?.colorHexList[0]}`,
                      }}
                    />
                    <span className='ken-configurator__carousel--color-swatch-item-label'>
                      {variant?.colorName}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {slides[carouselIndex].price ||
            (slides[carouselIndex]?.activeVariant && (
              <div className='ken-configurator__carousel--slide-price'>
                {t(
                  'configurator:configurator.productscarousel.slide.price.label',
                )}
                :&nbsp;
                {isOwner
                  ? slides[carouselIndex].price.formattedValue
                  : slides[carouselIndex].activeVariant.price.formattedValue}
              </div>
            ))}
          <ConnectionFlow slideData={slides[carouselIndex]} />
        </div>
        <div className='ken-configurator__carousel--pagination'>
          <div className='swiper-pagination' />
        </div>
      </div>
    )
  );
};

export default ProductsCarousel;
